import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";
import axiosInstance from "../axiosInstance";
import {
  EmptyState,
  PortalLoading,
  StatusBadge,
  TeacherPageHeader,
} from "./teacherPortalUtils";

const emptyAssessment = {
  session: "",
  class_subject: "",
  assessment_type: "",
  max_score: "100",
  weight: "",
  due_date: "",
  status: "OPEN",
  notes: "",
};

function TeacherMarksPage() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const [assessments, setAssessments] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [assessmentTypes, setAssessmentTypes] = useState([]);
  const [allocations, setAllocations] = useState([]);

  const [selected, setSelected] = useState("");
  const [assessment, setAssessment] = useState(null);
  const [rows, setRows] = useState([]);

  const [createForm, setCreateForm] = useState(emptyAssessment);

  const loadAssessments = async () => {
    try {
      setLoading(true);

      const response = await axiosInstance.get("teacher-portal/marks/");
      const payload = response.data || {};

      setAssessments(payload.assessments || []);
      setSessions(payload.sessions || []);
      setAssessmentTypes(payload.assessment_types || []);
      setAllocations(payload.allocations || []);
    } catch (error) {
      toast.error("Failed to load teacher assessments.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadAssessments();
  }, []);

  const updateCreateForm = (field, value) => {
    setCreateForm((current) => {
      const next = { ...current, [field]: value };

      if (field === "assessment_type") {
        const type = assessmentTypes.find(
          (item) => String(item.id) === String(value)
        );

        if (type?.weight) {
          next.weight = type.weight;
        }
      }

      return next;
    });
  };

  const createAssessment = async (event) => {
    event.preventDefault();

    if (
      !createForm.session ||
      !createForm.class_subject ||
      !createForm.assessment_type
    ) {
      return toast.warning(
        "Select session, class/subject and assessment type."
      );
    }

    try {
      setSaving(true);

      await axiosInstance.post("teacher-portal/marks/", {
        action: "CREATE_ASSESSMENT",
        ...createForm,
      });

      toast.success("Assessment created successfully.");
      setCreateForm(emptyAssessment);
      await loadAssessments();
    } catch (error) {
      toast.error(
        error.response?.data?.detail || "Failed to create assessment."
      );
    } finally {
      setSaving(false);
    }
  };

  const loadRows = async (id) => {
    setSelected(id);
    setRows([]);
    setAssessment(null);

    if (!id) return;

    try {
      setLoading(true);

      const response = await axiosInstance.get(
        `teacher-portal/marks/?assessment=${id}`
      );

      setAssessment(response.data?.assessment || null);

      const existingMarks = response.data?.marks || [];

      setRows(
        (response.data?.students || []).map((student) => {
          const mark = existingMarks.find(
            (item) => String(item.student) === String(student.id)
          );

          return {
            student: student.id,
            student_name: student.full_name,
            student_number: student.student_number,
            raw_score: mark?.raw_score ?? "",
            is_absent: Boolean(mark?.is_absent),
            remark: mark?.remark || "",
            grade: mark?.grade || "",
            percentage: mark?.percentage || "",
            status: mark?.status || "SUBMITTED",
          };
        })
      );
    } catch (error) {
      toast.error(
        error.response?.data?.detail || "Failed to load assessment students."
      );
    } finally {
      setLoading(false);
    }
  };

  const updateRow = (studentId, field, value) => {
    setRows((current) =>
      current.map((row) =>
        String(row.student) === String(studentId)
          ? { ...row, [field]: value }
          : row
      )
    );
  };

  const saveMarks = async (event) => {
    event.preventDefault();

    if (!selected) {
      return toast.warning("Choose an assessment first.");
    }

    try {
      setSaving(true);

      await axiosInstance.post("teacher-portal/marks/", {
        action: "SAVE_MARKS",
        assessment: selected,
        marks: rows.map((row) => ({
          student: row.student,
          raw_score: row.is_absent ? null : row.raw_score,
          is_absent: row.is_absent,
          remark: row.remark,
          status: row.status,
        })),
      });

      toast.success("Marks saved successfully.");
      await loadRows(selected);
    } catch (error) {
      toast.error(
        error.response?.data?.detail || "Failed to save marks."
      );
    } finally {
      setSaving(false);
    }
  };

  if (loading && !assessments.length && !selected) {
    return <PortalLoading label="Loading assessments..." />;
  }

  return (
    <div className="container-fluid py-4">
      <TeacherPageHeader
        title="Assessments & Marks"
        subtitle="Create assessments and capture marks for the classes and subjects assigned to you."
        icon="bi-pencil-square"
        actions={
          <button
            className="btn btn-outline-success"
            onClick={loadAssessments}
          >
            Refresh
          </button>
        }
      />

      <div className="card border-0 shadow-sm mb-4">
        <div className="card-header bg-white py-3">
          <h5 className="fw-bold mb-1">Create Assessment</h5>
          <small className="text-muted">
            Monthly tests, termly assessments, class work and assignments can
            be created for your allocated subjects.
          </small>
        </div>

        <div className="card-body">
          {sessions.length === 0 ? (
            <div className="alert alert-warning mb-0">
              There is currently no open assessment session.
            </div>
          ) : (
            <form className="row g-3" onSubmit={createAssessment}>
              <div className="col-md-4">
                <label className="form-label fw-semibold">
                  Assessment Session
                </label>
                <select
                  className="form-select"
                  value={createForm.session}
                  onChange={(e) =>
                    updateCreateForm("session", e.target.value)
                  }
                  required
                >
                  <option value="">Select session</option>
                  {sessions.map((item) => (
                    <option key={item.id} value={item.id}>
                      {item.name} - {item.term}
                    </option>
                  ))}
                </select>
              </div>

              <div className="col-md-4">
                <label className="form-label fw-semibold">
                  Class & Subject
                </label>
                <select
                  className="form-select"
                  value={createForm.class_subject}
                  onChange={(e) =>
                    updateCreateForm("class_subject", e.target.value)
                  }
                  required
                >
                  <option value="">Select class and subject</option>
                  {allocations.map((item) => (
                    <option key={item.id} value={item.class_subject}>
                      {item.class_group_name} - {item.subject_name}
                    </option>
                  ))}
                </select>
              </div>

              <div className="col-md-4">
                <label className="form-label fw-semibold">
                  Assessment Type
                </label>
                <select
                  className="form-select"
                  value={createForm.assessment_type}
                  onChange={(e) =>
                    updateCreateForm("assessment_type", e.target.value)
                  }
                  required
                >
                  <option value="">Select assessment type</option>
                  {assessmentTypes.map((item) => (
                    <option key={item.id} value={item.id}>
                      {item.name}
                    </option>
                  ))}
                </select>
              </div>

              <div className="col-md-3">
                <label className="form-label fw-semibold">
                  Maximum Mark
                </label>
                <input
                  type="number"
                  min="1"
                  step="0.01"
                  className="form-control"
                  value={createForm.max_score}
                  onChange={(e) =>
                    updateCreateForm("max_score", e.target.value)
                  }
                  required
                />
              </div>

              <div className="col-md-3">
                <label className="form-label fw-semibold">Weight %</label>
                <input
                  type="number"
                  min="0.01"
                  step="0.01"
                  className="form-control"
                  value={createForm.weight}
                  onChange={(e) =>
                    updateCreateForm("weight", e.target.value)
                  }
                  placeholder="Assessment weight"
                />
              </div>

              <div className="col-md-3">
                <label className="form-label fw-semibold">Due Date</label>
                <input
                  type="date"
                  className="form-control"
                  value={createForm.due_date}
                  onChange={(e) =>
                    updateCreateForm("due_date", e.target.value)
                  }
                />
              </div>

              <div className="col-md-3">
                <label className="form-label fw-semibold">Status</label>
                <select
                  className="form-select"
                  value={createForm.status}
                  onChange={(e) =>
                    updateCreateForm("status", e.target.value)
                  }
                >
                  <option value="OPEN">Open for Marks</option>
                  <option value="DRAFT">Draft</option>
                </select>
              </div>

              <div className="col-12">
                <label className="form-label fw-semibold">Notes</label>
                <textarea
                  className="form-control"
                  rows="2"
                  value={createForm.notes}
                  onChange={(e) =>
                    updateCreateForm("notes", e.target.value)
                  }
                  placeholder="Optional assessment notes"
                />
              </div>

              <div className="col-12">
                <button
                  className="btn btn-success"
                  disabled={saving}
                >
                  {saving ? "Creating..." : "Create Assessment"}
                </button>
              </div>
            </form>
          )}
        </div>
      </div>

      <div className="card border-0 shadow-sm">
        <div className="card-header bg-white py-3">
          <h5 className="fw-bold mb-0">Marks Entry</h5>
        </div>

        <div className="card-body">
          <form onSubmit={saveMarks}>
            <div className="row g-3 mb-3">
              <div className="col-md-4">
                <label className="form-label fw-semibold">
                  Assessment
                </label>
                <select
                  className="form-select"
                  value={selected}
                  onChange={(e) => loadRows(e.target.value)}
                >
                  <option value="">Choose assessment</option>
                  {assessments.map((item) => (
                    <option key={item.id} value={item.id}>
                      {item.session_name} - {item.class_group_name} -{" "}
                      {item.subject_name} ({item.assessment_type_name})
                    </option>
                  ))}
                </select>
              </div>

              <div className="col-md-8 d-flex align-items-end">
                {assessment && (
                  <div className="alert alert-light border mb-0 w-100">
                    <strong>{assessment.subject_name}</strong> ·{" "}
                    {assessment.class_group_name} ·{" "}
                    {assessment.assessment_type_name} · Max:{" "}
                    {assessment.max_score} ·{" "}
                    <StatusBadge value={assessment.status} />
                  </div>
                )}
              </div>
            </div>

            {rows.length ? (
              <div className="table-responsive">
                <table className="table table-hover align-middle">
                  <thead className="table-light">
                    <tr>
                      <th>Student</th>
                      <th>Score</th>
                      <th>Absent</th>
                      <th>Remark</th>
                      <th>Grade</th>
                      <th>Status</th>
                    </tr>
                  </thead>

                  <tbody>
                    {rows.map((row) => (
                      <tr key={row.student}>
                        <td>
                          <strong>{row.student_name}</strong>
                          <div className="small text-muted">
                            {row.student_number}
                          </div>
                        </td>

                        <td style={{ width: 140 }}>
                          <input
                            className="form-control form-control-sm"
                            type="number"
                            min="0"
                            max={assessment?.max_score || undefined}
                            step="0.01"
                            value={row.raw_score}
                            disabled={row.is_absent}
                            onChange={(e) =>
                              updateRow(
                                row.student,
                                "raw_score",
                                e.target.value
                              )
                            }
                          />
                        </td>

                        <td>
                          <input
                            type="checkbox"
                            className="form-check-input"
                            checked={row.is_absent}
                            onChange={(e) =>
                              updateRow(
                                row.student,
                                "is_absent",
                                e.target.checked
                              )
                            }
                          />
                        </td>

                        <td>
                          <input
                            className="form-control form-control-sm"
                            value={row.remark}
                            onChange={(e) =>
                              updateRow(
                                row.student,
                                "remark",
                                e.target.value
                              )
                            }
                          />
                        </td>

                        <td>{row.grade || "-"}</td>

                        <td>
                          <StatusBadge value={row.status} />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <EmptyState
                title="Choose an assessment"
                text="The students in that class will load here."
              />
            )}

            <button
              className="btn btn-success mt-3"
              disabled={saving || !rows.length}
            >
              {saving ? "Saving..." : "Save Marks"}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}

export default TeacherMarksPage;
